import { useEffect, useState } from 'react';
import { onboardingAPI, intelligenceAPI } from '../../lib/api';
import type { FilledDataset, SupplierQualification } from '../../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Loader2 } from 'lucide-react';
import { formatDateTime } from '../../lib/utils';
import { STATUS_LABELS, type SupplierQuestionnaireRow } from './rows';

interface HistoryDrawerProps {
  row: SupplierQuestionnaireRow | null;
  onClose: () => void;
}

export interface HistoryEvent {
  key: string;
  at: string | null;
  label: string;
  detail?: string | null;
  by?: string | null;
}

const outcomeLabel = (rawStatus: string): string => {
  switch (rawStatus) {
    case 'approved': return 'Approved';
    case 'rejected': return 'Rejected';
    case 'more_info_required': return 'More information requested';
    case 'under_review': return 'Under review';
    default: return '';
  }
};

/** The fixed milestones every questionnaire passes through, from the row's dates. */
export function milestoneEvents(row: SupplierQuestionnaireRow, sentBy: string | null, submittedBy: string | null): HistoryEvent[] {
  const events: HistoryEvent[] = [];
  if (row.assignedAt) events.push({ key: 'sent', at: row.assignedAt, label: 'Sent', by: sentBy });
  if (row.startedAt) events.push({ key: 'started', at: row.startedAt, label: 'Started' });
  if (row.submittedAt) events.push({ key: 'submitted', at: row.submittedAt, label: 'Submitted', by: submittedBy });
  if (row.reviewedAt) {
    events.push({
      key: 'reviewed',
      at: row.reviewedAt,
      label: 'Customer review',
      detail: [outcomeLabel(row.rawStatus), row.feedback].filter(Boolean).join(' · ') || null,
    });
  }
  return events;
}

/** Events recorded by the platform for onboarding-side rows. */
function datasetActivity(detail: FilledDataset): HistoryEvent[] {
  return (detail.activity_log ?? []).map((entry) => ({
    key: `activity-${entry.id}`,
    at: entry.performed_at,
    label: entry.description || entry.action.replace(/_/g, ' '),
    by: entry.performed_by,
  }));
}

/** Decisions recorded on Supplier Intelligence rows, including the supplier's own replies. */
function qualificationActivity(detail: SupplierQualification): HistoryEvent[] {
  return (detail.approvals ?? []).map((approval) => ({
    key: `approval-${approval.id}`,
    at: approval.reviewed_at ?? approval.assigned_at,
    label: approval.decision_display ?? approval.decision.replace(/_/g, ' '),
    detail: approval.comments || null,
    by: approval.approver_name ?? null,
  }));
}

const time = (iso: string | null): number => (iso ? new Date(iso).getTime() || 0 : 0);

/**
 * CR-5865: the audit history for one questionnaire, as the supplier may see it.
 *
 * Milestones (sent, started, submitted, customer review) come from the row's
 * own dates; the finer-grained entries come from the detail endpoint each
 * system already exposes to the supplier — `activity_log` on an onboarding
 * dataset, `approvals` on a qualification. Nothing is fetched until opened.
 */
export function HistoryDrawer({ row, onClose }: HistoryDrawerProps) {
  const [events, setEvents] = useState<HistoryEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!row) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      let sentBy: string | null = null;
      let submittedBy: string | null = null;
      let activity: HistoryEvent[] = [];
      if (row.kind === 'dataset') {
        const detail = (await onboardingAPI.getFilledDataset(row.id)).data;
        activity = datasetActivity(detail);
      } else {
        const detail = (await intelligenceAPI.getQualification(row.id)).data;
        sentBy = detail.invited_by_name ?? null;
        submittedBy = detail.completed_by_name ?? null;
        activity = qualificationActivity(detail);
      }
      return [...milestoneEvents(row, sentBy, submittedBy), ...activity]
        .sort((a, b) => time(a.at) - time(b.at));
    };

    load()
      .then((list) => { if (!cancelled) setEvents(list); })
      .catch((err: unknown) => {
        console.error('Failed to load questionnaire history:', err);
        if (!cancelled) {
          // The milestones need no fetch, so show them even when the detail failed.
          setEvents(milestoneEvents(row, null, null));
          setError('Some history could not be loaded.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [row]);

  return (
    <Dialog open={row !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{row?.name ?? 'Questionnaire'} — history</DialogTitle>
          <DialogDescription>
            {row?.customerName ? `${row.customerName} · ` : ''}
            {row ? STATUS_LABELS[row.status] : ''}
            {row?.showScore && row.scorePercentage != null && (
              <> · Score {Math.round(row.scorePercentage)}%{row.passed != null ? (row.passed ? ' · Passed' : ' · Failed') : ''}</>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
          </div>
        ) : events.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">No history recorded yet.</p>
        ) : (
          <ol className="relative border-l border-border ml-2 space-y-4 py-2" data-testid="questionnaire-history">
            {events.map((event) => (
              <li key={event.key} className="ml-4">
                <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-background bg-primary" />
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium capitalize">{event.label}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {event.at ? formatDateTime(event.at) : '—'}
                  </span>
                </div>
                {(event.by || event.detail) && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {event.by && <Badge variant="outline" className="mr-1.5">{event.by}</Badge>}
                    {event.detail}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
        {error && <p className="text-xs text-amber-700">{error}</p>}
      </DialogContent>
    </Dialog>
  );
}
